import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    TextField,
    Typography,
    Alert,
    CircularProgress,
    Paper,
    Container,
    InputAdornment,
    IconButton,
} from '@mui/material';
import {
    LocalHospital,
    Visibility,
    VisibilityOff,
    CheckCircle,
    Cancel,
} from '@mui/icons-material';
import { authService } from '../../services/authService';

interface PasswordRequirement {
    label: string;
    met: boolean;
}

export const ResetPasswordPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    // Password strength checker
    const passwordRequirements: PasswordRequirement[] = [
        { label: t('auth.signup.passwordMin8'), met: password.length >= 8 },
        { label: t('auth.signup.passwordUppercase'), met: /[A-Z]/.test(password) },
        { label: t('auth.signup.passwordLowercase'), met: /[a-z]/.test(password) },
        { label: t('auth.signup.passwordNumber'), met: /\d/.test(password) },
    ];

    const isPasswordValid = passwordRequirements.every((req) => req.met);
    const passwordsMatch = password === confirmPassword;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!token) {
            setError(t('auth.resetPassword.error'));
            return;
        }

        if (!isPasswordValid || !passwordsMatch) {
            return;
        }

        setLoading(true);

        try {
            await authService.resetPassword(token, password);
            setSuccess(true);
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (err: any) {
            setError(err.response?.data?.detail || t('auth.resetPassword.error'));
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <Container component="main" maxWidth="xs">
                <Box sx={{ marginTop: 8 }}>
                    <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
                        <Alert severity="error">{t('auth.resetPassword.error')}</Alert>
                        <Button
                            component={Link}
                            to="/login"
                            variant="outlined"
                            sx={{ mt: 2 }}
                        >
                            {t('auth.forgotPassword.backToLogin')}
                        </Button>
                    </Paper>
                </Box>
            </Container>
        );
    }

    return (
        <Container component="main" maxWidth="xs">
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Paper elevation={3} sx={{ p: 4, width: '100%', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <LocalHospital sx={{ fontSize: 48, color: 'primary.main' }} />
                    </Box>

                    <Typography variant="h5" component="h1" gutterBottom textAlign="center">
                        {t('auth.resetPassword.title')}
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {success ? (
                        <Box>
                            <Alert severity="success" sx={{ mb: 2 }}>
                                {t('auth.resetPassword.success')}
                            </Alert>
                            <Typography variant="body2" textAlign="center">
                                Redirection vers la connexion...
                            </Typography>
                        </Box>
                    ) : (
                        <Box component="form" onSubmit={handleSubmit} noValidate>
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="password"
                                label={t('auth.resetPassword.newPassword')}
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                autoComplete="new-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={loading}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            {/* Password requirements */}
                            {password && (
                                <Box sx={{ mt: 1, mb: 2 }}>
                                    {passwordRequirements.map((req, index) => (
                                        <Box
                                            key={index}
                                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                        >
                                            {req.met ? (
                                                <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                                            ) : (
                                                <Cancel sx={{ fontSize: 16, color: 'error.main' }} />
                                            )}
                                            <Typography
                                                variant="caption"
                                                color={req.met ? 'success.main' : 'error.main'}
                                            >
                                                {req.label}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            )}

                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="confirmPassword"
                                label={t('auth.resetPassword.confirmPassword')}
                                type={showPassword ? 'text' : 'password'}
                                id="confirmPassword"
                                autoComplete="new-password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                disabled={loading}
                                error={confirmPassword !== '' && !passwordsMatch}
                                helperText={
                                    confirmPassword !== '' && !passwordsMatch
                                        ? t('auth.signup.passwordMismatch')
                                        : ''
                                }
                            />

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{ mt: 3, mb: 2 }}
                                disabled={loading || !isPasswordValid || !passwordsMatch}
                            >
                                {loading ? <CircularProgress size={24} /> : t('auth.resetPassword.submit')}
                            </Button>
                        </Box>
                    )}
                </Paper>
            </Box>
        </Container>
    );
};
